import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Socket as PhoenixSocket } from 'phoenix';
import { create as createAbsintheSocket } from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import { ApolloLink } from '@apollo/client/core';
import { BehaviorSubject, filter } from 'rxjs';

import { AppState } from '@state/app.state';
import { selectAuthToken } from '@state/auth/auth.selectors';
import { environment } from 'src/environments/environment';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class SocketService {
  private socket?: PhoenixSocket;
  private wsLink?: ApolloLink;

  private currentToken?: string;
  private readySubject = new BehaviorSubject<boolean>(false);
  public readonly ready$ = this.readySubject.asObservable();

  private linkSubject = new BehaviorSubject<ApolloLink | undefined>(undefined);
  public readonly link$ = this.linkSubject
    .asObservable()
    .pipe(
      filter((l) => !!l)
    ) as unknown as import('rxjs').Observable<ApolloLink>;

  constructor(private store: Store<AppState>) {
    this.store
      .select(selectAuthToken)
      .pipe(untilDestroyed(this))
      .subscribe((token) => {
        if (token) {
          if (token !== this.currentToken) {
            this.startSocket(token);
          }
        } else {
          this.stopSocket();
        }
      });
  }

  private buildEndpoint(): string {
    const base = new URL(environment.api);
    const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${base.host}/socket`;
  }

  private startSocket(token: string) {
    // If we already have a socket with the same token, do nothing
    if (this.socket && this.currentToken === token) {
      return;
    }

    // If there's an existing socket with a different token, stop it first
    if (this.socket && this.currentToken !== token) {
      this.stopSocket();
    }

    this.currentToken = token;
    this.readySubject.next(false);
    this.linkSubject.next(undefined);

    this.socket = new PhoenixSocket(this.buildEndpoint(), {
      params: { Authorization: `Bearer ${token}` },
      // Allow reconnects (progressive backoff up to 10s)
      reconnectAfterMs: (tries: number) => Math.min(10_000, tries * 1000),
    });

    this.socket.onOpen(() => {
      console.log('Socket opened');
      // Create Absinthe link only after open to avoid races
      const absintheSocket = createAbsintheSocket(this.socket!);
      this.wsLink = createAbsintheSocketLink(
        absintheSocket
      ) as unknown as ApolloLink;
      this.linkSubject.next(this.wsLink);
      this.readySubject.next(true);
    });

    this.socket.onClose((ev) => {
      console.log('Socket closed:', ev);
      this.readySubject.next(false);
      this.linkSubject.next(undefined);
    });

    this.socket.onError((err) => {
      console.log('Socket error:', err);
    });

    this.socket.connect();
  }

  private stopSocket() {
    if (this.socket) {
      try {
        this.socket.disconnect(
          () => {
            console.log('Socket disconnected');
          },
          1000,
          'Manual disconnect'
        );
      } catch (e) {
        console.warn('Error while disconnecting socket', e);
      }
    }
    this.socket = undefined;
    this.wsLink = undefined;
    this.currentToken = undefined;
    this.readySubject.next(false);
    this.linkSubject.next(undefined);
  }

  getLink(): ApolloLink | undefined {
    return this.wsLink;
  }
}
